import { Link } from "react-router-dom";
import { ArrowRight, Home, Phone, MapPin, Wrench } from "lucide-react";
import { COMPANY } from "../data/site";
import { useReveal } from "../hooks/use-reveal";
import { useSeo } from "../hooks/use-seo";

const POPULAR_LINKS = [
    { to: "/services", label: "All Services", icon: Wrench },
    { to: "/service-areas", label: "Service Areas", icon: MapPin },
    { to: "/reviews", label: "Reviews" },
    { to: "/about", label: "About HAZ" },
    { to: "/contractors", label: "For Contractors" },
    { to: "/sitemap", label: "Sitemap" },
];

export default function NotFound() {
    useReveal();
    useSeo({
        title: "Page Not Found · HAZ Insulation",
        description:
            "We couldn't find that page. Browse HAZ Insulation services, service areas, or request a free quote.",
    });

    return (
        <main data-testid="page-not-found" className="pt-24">
            <section className="relative py-24 md:py-36 bg-ink text-bone overflow-hidden">
                <div className="absolute inset-0 grain pointer-events-none" />
                <div className="max-w-5xl mx-auto px-6 md:px-12 relative">
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-ember mb-6 reveal">
                        404 · Page not found
                    </div>
                    <h1 className="font-heading text-5xl sm:text-6xl lg:text-8xl font-extrabold tracking-tight leading-[0.95] reveal">
                        We couldn't <span className="text-ember">find that page.</span>
                    </h1>
                    <p className="mt-8 text-lg md:text-xl text-bone/85 max-w-3xl leading-relaxed reveal">
                        The link may be old, mistyped, or moved. Try one of the popular pages
                        below — or get a free quote and we'll get back to you the same day.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4 reveal">
                        <Link
                            to="/contact"
                            className="btn-lift inline-flex items-center justify-center gap-2 bg-copper text-bone font-semibold py-4 px-8 hover:bg-copper-hover"
                        >
                            Get a free quote <ArrowRight size={18} />
                        </Link>
                        <Link
                            to="/"
                            className="btn-lift inline-flex items-center justify-center gap-2 border-2 border-bone text-bone font-semibold py-4 px-8 hover:bg-bone hover:text-ink"
                        >
                            <Home size={16} /> Back to home
                        </Link>
                        <a
                            href={COMPANY.phoneHref}
                            className="btn-lift inline-flex items-center justify-center gap-2 border-2 border-bone/40 text-bone font-semibold py-4 px-8 hover:bg-bone hover:text-ink"
                        >
                            <Phone size={16} /> {COMPANY.phone}
                        </a>
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-28 bg-bone">
                <div className="max-w-5xl mx-auto px-6 md:px-12">
                    <div className="text-xs tracking-[0.22em] uppercase font-bold text-moss mb-5 reveal">
                        Popular pages
                    </div>
                    <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-10 reveal">
                        Where would you like to go?
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-line border border-line reveal">
                        {POPULAR_LINKS.map((link) => {
                            const Icon = link.icon;
                            return (
                                <Link
                                    key={link.to}
                                    to={link.to}
                                    className="bg-white p-7 hover:bg-ink hover:text-bone group transition-colors"
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="font-heading font-bold text-lg group-hover:text-ember transition-colors">
                                            {link.label}
                                        </span>
                                        {Icon ? (
                                            <Icon
                                                size={18}
                                                strokeWidth={1.5}
                                                className="text-copper"
                                            />
                                        ) : (
                                            <ArrowRight
                                                size={18}
                                                className="text-copper opacity-0 group-hover:opacity-100 transition-opacity"
                                            />
                                        )}
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>
        </main>
    );
}
